import React, { useEffect, useState } from 'react';
import { getCompanies } from '../../api/company-api';
import { useSearchParams } from 'react-router-dom';
import SearchComponent from '../searchComponent';
import Overlay from '../overlays/editCompany';
import { useConfig } from '../../providers/configProvider';
import { useLanguage } from '../../providers/preferenceProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPenToSquare, faPlus, faCaretUp, faCaretDown } from '@fortawesome/free-solid-svg-icons'
import { ACL } from '../../acls';
import { getRoles } from "../../api/role-api";

const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [showOverlay, setShowOverlay] = useState(false);
  const [overlayCompany, setOverlayCompany] = useState(null);
  const [createNew, setCreateNew] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentSort = searchParams.get('companies_sort') || 'name:ASC';
  const [sortKey, sortDirection] = currentSort.split(':');
  const pageSize = 10;
  const [hasMore, setHasMore] = useState(true);
  const [count, setCount] = useState(0);
  const { isAuthorized } = useConfig();
  const { t } = useLanguage();
  const [roles, setRoles] = useState([]);

  function getSearchParams(searchParams, pageSize, refresh, companies) {
    const urlParams = new URLSearchParams();
    urlParams.append('search', searchParams.get('companies_search') || '');
    urlParams.append('sort', searchParams.get('companies_sort') || 'name:ASC');
    urlParams.append('pageSize', pageSize);
    urlParams.append('page', refresh ? 1 : Math.ceil(companies.length / pageSize) + 1);
    return urlParams;
  }

  async function fetchRoles() {
    getRoles().then((response) => {
      setRoles(response.data);
    });
  }

  useEffect(() => {
    fetchRoles();
  }, []);

  const getRoleName = (roleId) => {
    const role = roles.find((r) => r.id === roleId);
    return role ? role.name : roleId;
  };

  async function fetchData(refresh = false) {
    let localCompanies = [...companies];
    if (refresh) {
      localCompanies = [];
    }
    const urlParams = getSearchParams(searchParams, pageSize, refresh, localCompanies);
    try {
      const response = await getCompanies(urlParams);
      if (response.data && Array.isArray(response.data.rows)) {
        setCompanies([...localCompanies, ...response.data.rows]);
        setHasMore(localCompanies.length + response.data.rows.length < response.data.count);
        setCount(response.data.count);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  }

  useEffect(() => {
    fetchData(true);
    // eslint-disable-next-line
  }, [searchParams]);

  function setSearch(query) {
    searchParams.set('companies_search', query);
    setSearchParams(searchParams);
  }

  function setSort(key) {
    let newDirection = 'DESC';
    const current = searchParams.get('companies_sort')?.split(':');
    if (current && current[0] === key) {
      newDirection = current[1] === 'ASC' ? 'DESC' : 'ASC';
    }
    searchParams.set('companies_sort', `${key}:${newDirection}`);
    setSearchParams(searchParams);
  }

  const handleEditCompany = (company) => {
    setCreateNew(false);
    setOverlayCompany(company);
    setShowOverlay(true);
  };

  const handleAddCompany = () => {
    setCreateNew(true);
    setOverlayCompany(null);
    setShowOverlay(true);
  };

  return (
    <>
      <div className="admin-list-wrapper">
        <div className="flex md:flex-row flex-col gap-2 mb-2 justify-between"> 
          <SearchComponent onSearchChange={setSearch}></SearchComponent>
          <button
            className="admin-add-button" 
            disabled={!isAuthorized(ACL.COMPANY)}
            onClick={handleAddCompany}
          >
            {t.add + " " + t.company} <FontAwesomeIcon icon={faPlus} />
          </button>
        </div>
        <div className="admin-list-header mb-4 flex justify-between items-center">
          <div>{t.amount_of + " " + t.companies + ": " + count}</div>
          
        </div>
        <div className="admin-list-box">
          <div className="admin-list-header grid-cols-4 px-4 py-2">
            <div onClick={() => setSort('name')} className="cursor-pointer select-none">
              {t.company}
              {sortKey === 'name' && (<FontAwesomeIcon icon={sortDirection === 'ASC' ? faCaretUp : faCaretDown} className="table-sort-icon" />)}
            </div>
            <div onClick={() => setSort('userCount')} className="cursor-pointer select-none">
              {t.users}
              {sortKey === 'userCount' && (<FontAwesomeIcon icon={sortDirection === 'ASC' ? faCaretUp : faCaretDown} className="table-sort-icon" />)}
            </div>
            <div onClick={() => setSort('defaultRoleId')} className="cursor-pointer select-none">
              {t.role}
              {sortKey === 'defaultRoleId' && (<FontAwesomeIcon icon={sortDirection === 'ASC' ? faCaretUp : faCaretDown} className="table-sort-icon" />)}
            </div>
          </div>
          {companies && companies.map((company) => (
            <div
              key={company.id}
              className="grid grid-cols-1 md:grid-cols-4 items-center px-3 py-2 rounded-md m-2 bg-[var(--white-color)]"
            >
              <div className="flex items-center gap-3 ml-1">
                <img loading='lazy' src={company.image} alt={company.name} className="w-10 h-10 rounded-md object-cover" />
                <span className="font-medium">{company.name}</span>
              </div>
              <div >
                <span className="block md:hidden">{t.amount_of + " " + t.users + ": "}</span>
                {company.userCount}/{company.maxUsers}
              </div>
              <div>
                <span className="block md:hidden">{t.default_role + ": "}</span>
                {getRoleName(company.defaultRoleId)}
              </div>
              <div className="text-right">
                <button 
                  className="admin-edit-button"
                  disabled={!isAuthorized(ACL.COMPANY)}
                  onClick={() => handleEditCompany(company)}
                >
                  {t.edit} <FontAwesomeIcon icon={faPenToSquare} />
                </button>
              </div>
            </div>
          ))}
          {hasMore && (
            <button className="admin-edit-button" onClick={() => fetchData()}>
              {t.show_more}
            </button>
          )}
          <div className="grid grid-cols-4 px-4 py-2">
            <div>
              <h2>{t.showing.replace("{ph_length}", companies.length).replace("{ph_count}", count)}</h2>
            </div>
          </div>
        </div>
      </div>
      {showOverlay && (
        <Overlay
          isOpen={showOverlay}
          onClose={() => setShowOverlay(false)}
          company={overlayCompany}
          createNew={createNew}
          onRefresh={fetchData}
        />
      )}
    </>
  );
};

export default Companies;
